'use client';

import { type Node } from '@react-types/shared';
import classNames from 'classnames';
import { useRef } from 'react';
import {
  type AriaTabListProps,
  type AriaTabPanelProps,
  useTab,
  useTabList,
  useTabPanel,
} from 'react-aria';
import Skeleton from 'react-loading-skeleton';
import { Item, type TabListState, useTabListState } from 'react-stately';

import { Button } from '@module/mdrt-org/shared/components/button';
import { ButtonVariant } from '@shared/ui-components';

import { ToolkitItemCard } from './toolkit-item-card/toolkit-item-card';
import styles from './membership-toolkit-tabs-section.module.scss';
import {
  GTM_ACTION_CLICK,
  GTM_EVENT_NAME_FILE_DOWNLOAD,
  GTM_EVENT_NAME_INTERNAL_LINK_CLICK,
  GTM_EVENT_USER_INTERACTIONS,
  sendGtmEvent,
} from '@shared/utils';
import { MembershipToolkitTabs } from '@module/mdrt-org/shared/utils/types';

type LinksList = {
  foundationDonorLevel: string;
  items: Array<{
    displayText: string;
    downloadFileName: string;
    linkToContent: string;
    shouldDownload: boolean | null;
    target: string;
  }>;
  memberType: string;
  membershipLevel: string;
  preferredLanguage: string;
  title: string;
};

type TabsData = {
  description: string;
  key: string;
  layout: 'Images' | 'Links' | '';
  sections: LinksList[] | null;
  title: string;
};

export enum LinksListSectionTheme {
  Default = 'default',
  Secondary = 'secondary',
}

const Tab = ({ item, state }: { item: Node<TabsData>; state: TabListState<TabsData> }) => {
  const { key, value } = item;
  const ref = useRef(null);
  const { isSelected, tabProps } = useTab({ key }, state, ref);

  const sendMemberToolkitIntoGTM = () => {
    sendGtmEvent({
      event: GTM_EVENT_USER_INTERACTIONS,
      action: GTM_ACTION_CLICK,
      eventName: GTM_EVENT_NAME_INTERNAL_LINK_CLICK,
      link_label: 'Membership Kit',
      label: value?.title,
    });
  };

  return (
    <button
      className={classNames(styles['membership-toolkit-tabs__tab-item'], {
        [styles['membership-toolkit-tabs__tab-item--theme-light']]: !isSelected,
        [styles['membership-toolkit-tabs__tab-item--theme-active']]: isSelected,
      })}
      data-testid="membership-toolkit-tab-item"
      key={value?.key}
      ref={ref}
      type="button"
      {...tabProps}
      onClick={sendMemberToolkitIntoGTM}
    >
      {value?.title}
    </button>
  );
};

const TabPanel = ({ state, ...props }: AriaTabPanelProps & { state: TabListState<TabsData> }) => {
  const ref = useRef(null);
  const { tabPanelProps } = useTabPanel(props, state, ref);

  return (
    <div
      className={styles['membership-toolkit-tabs__content']}
      data-testid="membership-toolkit-tab-content"
      ref={ref}
      {...tabPanelProps}
    >
      {state.selectedItem?.props.children}
    </div>
  );
};

const Tabs = (props: AriaTabListProps<TabsData>) => {
  const state = useTabListState(props);
  const ref = useRef(null);
  const { tabListProps } = useTabList(props, state, ref);

  return (
    <div className={styles['membership-toolkit-tabs']}>
      {state.selectedItem ? (
        <div
          className={styles['membership-toolkit-tabs__list']}
          data-testid="membership-toolkit-tab-navigation"
          ref={ref}
          {...tabListProps}
        >
          {[...state.collection].map((item) => (
            <Tab item={item} key={item.key} state={state} />
          ))}
        </div>
      ) : (
        <div className={styles['membership-toolkit-tabs__list']}>
          {Array.from({ length: 6 }, (_, index) => index + 1).map((key) => (
            <Skeleton
              borderRadius={24}
              containerClassName={styles['membership-toolkit-tabs__item-skeleton']}
              height={39}
              key={key}
            />
          ))}
        </div>
      )}
      {state.selectedItem ? (
        <TabPanel key={state.selectedItem?.key} state={state} />
      ) : (
        <div className={styles['membership-toolkit-tabs__content']}>
          <Skeleton height={32} width={200} />
          <Skeleton
            containerClassName={styles['membership-toolkit-tabs__description-skeleton']}
            height={90}
          />
          <div className={styles['membership-toolkit-links-list']}>
            <Skeleton
              containerClassName={styles['membership-toolkit-links-list__item-skeleton']}
              height={120}
            />
            <Skeleton
              containerClassName={styles['membership-toolkit-links-list__item-skeleton']}
              height={120}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const LinksListSection = ({
  layout,
  list,
  MembershipToolkitTabsSectionTitle,
  sendMemberToolkitIntoGTM,
}: {
  layout: LinksListSectionTheme;
  list: LinksList | null;
  MembershipToolkitTabsSectionTitle: string;
  sendMemberToolkitIntoGTM: (data: MembershipToolkitTabs) => void;
}) => {
  if (list === null || list.items.length === 0) {
    return null;
  }

  const filteredItems = list.items.filter((link) => {
    if (layout === LinksListSectionTheme.Default) {
      return Boolean(link.displayText || link.linkToContent);
    }

    return Boolean(link.linkToContent);
  });

  if (filteredItems.length === 0) {
    return null;
  }

  return (
    <div>
      {list.title && (
        <h4 className={styles['membership-toolkit-links-list__title']}>{list.title}</h4>
      )}
      <ul
        className={classNames(styles['membership-toolkit-links-list__group'], {
          [styles['membership-toolkit-links-list__group--secondary']]:
            layout === LinksListSectionTheme.Secondary,
        })}
      >
        {filteredItems.map((link, index) => {
          return (
            <li
              className={styles['membership-toolkit-links-list__group-item']}
              // eslint-disable-next-line react/no-array-index-key
              key={link.displayText + index}
            >
              {layout === LinksListSectionTheme.Secondary ? (
                <ToolkitItemCard
                  link={link}
                  MembershipToolkitTabsSectionTitle={MembershipToolkitTabsSectionTitle}
                  LinksListSectionTitle={list.title}
                  sendMemberToolkitIntoGTM={sendMemberToolkitIntoGTM}
                />
              ) : (
                <Button
                  isSmall
                  target={link.target}
                  variant={ButtonVariant.Tertiary}
                  onClick={() => {
                    const url =
                      link.shouldDownload && link.downloadFileName
                        ? `${link.linkToContent}?download=${link.downloadFileName}`
                        : link.linkToContent ?? '#';

                    window.open(url, link.target);
                    sendMemberToolkitIntoGTM({
                      MembershipToolkitTabsSectionTitle,
                      LinksListSectionTitle: list.title,
                      linkURL: url,
                    });
                  }}
                >
                  {link.displayText || link.linkToContent}
                </Button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export const MembershipToolkitTabsSection = ({
  tabsAriaLabel,
  tabsData,
}: {
  tabsAriaLabel?: string;
  tabsData?: TabsData[];
}) => {
  if (!tabsData || tabsData.length === 0) return null;

  const sendMemberToolkitIntoGTM = (data: MembershipToolkitTabs) => {
    sendGtmEvent({
      event: GTM_EVENT_USER_INTERACTIONS,
      action: GTM_ACTION_CLICK,
      eventName: GTM_EVENT_NAME_FILE_DOWNLOAD,
      link_label: 'Membership Kit',
      label: data?.MembershipToolkitTabsSectionTitle,
      carousel_header: data?.LinksListSectionTitle,
      link_url: data?.linkURL,
    });
  };

  return (
    <Tabs aria-label={tabsAriaLabel} items={tabsData}>
      {(itemData) => {
        const { description, layout, sections, title } = itemData;

        return (
          <Item>
            <h3>{title}</h3>
            {description && (
              <p className={styles['membership-toolkit-tabs__description']}>{description}</p>
            )}
            <div
              className={classNames(styles['membership-toolkit-links-list'], {
                [styles['membership-toolkit-links-list--secondary']]: layout === 'Images',
              })}
            >
              {sections?.map((sectionList, index) => (
                <LinksListSection
                  // eslint-disable-next-line react/no-array-index-key
                  key={sectionList.title.replaceAll(/\s+/gu, '-') + index}
                  layout={
                    layout === 'Images'
                      ? LinksListSectionTheme.Secondary
                      : LinksListSectionTheme.Default
                  }
                  list={sectionList}
                  MembershipToolkitTabsSectionTitle={title}
                  sendMemberToolkitIntoGTM={sendMemberToolkitIntoGTM}
                />
              ))}
            </div>
          </Item>
        );
      }}
    </Tabs>
  );
};
